import { supabase } from './supabaseClient';

// Phase 2a infrastructure API: thin wrappers around the streets / obstacles /
// poles RPCs added in migration 0016. All RPCs are SECURITY INVOKER and the
// RLS policies do the real work, so these functions stay transport-only.

// --- streets ---------------------------------------------------------------

export async function streetsInBbox(bounds) {
  const { data, error } = await supabase.rpc('street_segments_in_bbox', {
    min_lng: bounds.west,
    min_lat: bounds.south,
    max_lng: bounds.east,
    max_lat: bounds.north,
  });
  return { rows: data || [], error };
}

export async function createStreet(geometry, opts = {}) {
  const { data, error } = await supabase.rpc('create_street_segment', {
    g: geometry,
    p_class: opts.class || 'local',
    p_width: Number.isFinite(opts.width) ? opts.width : null,
    p_notes: opts.notes || null,
  });
  return { id: data, error };
}

export async function updateStreet(id, geometry, opts = {}) {
  const { error } = await supabase.rpc('update_street_segment', {
    b_id: id,
    g: geometry,
    p_class: opts.class || null,
    p_width: Number.isFinite(opts.width) ? opts.width : null,
    p_notes: opts.notes != null ? opts.notes : null,
  });
  return { error };
}

export async function deleteStreet(id) {
  const { error } = await supabase.from('street_segments').delete().eq('id', id);
  return { error };
}

// --- building entry points (lives on the buildings table) ------------------

// pass null `geometry` to clear the entry point.
export async function setBuildingEntry(buildingId, geometry) {
  const { error } = await supabase.rpc('set_building_entry', {
    b_id: buildingId,
    g: geometry,
  });
  return { error };
}

// --- obstacle zones --------------------------------------------------------

export async function obstaclesInBbox(bounds) {
  const { data, error } = await supabase.rpc('obstacle_zones_in_bbox', {
    min_lng: bounds.west,
    min_lat: bounds.south,
    max_lng: bounds.east,
    max_lat: bounds.north,
  });
  return { rows: data || [], error };
}

export async function createObstacle(geometry, opts = {}) {
  const { data, error } = await supabase.rpc('create_obstacle_zone', {
    g: geometry,
    p_kind: opts.kind || 'other',
    p_notes: opts.notes || null,
  });
  return { id: data, error };
}

export async function updateObstacle(id, geometry, opts = {}) {
  const { error } = await supabase.rpc('update_obstacle_zone', {
    b_id: id,
    g: geometry,
    p_kind: opts.kind || null,
    p_notes: opts.notes != null ? opts.notes : null,
  });
  return { error };
}

export async function deleteObstacle(id) {
  const { error } = await supabase.from('obstacle_zones').delete().eq('id', id);
  return { error };
}

// --- poles -----------------------------------------------------------------

export async function polesInBbox(bounds) {
  const { data, error } = await supabase.rpc('poles_in_bbox', {
    min_lng: bounds.west,
    min_lat: bounds.south,
    max_lng: bounds.east,
    max_lat: bounds.north,
  });
  return { rows: data || [], error };
}

export async function createPole(geometry, opts = {}) {
  const { data, error } = await supabase.rpc('create_pole', {
    g: geometry,
    p_kind: opts.kind || 'utility',
    p_height: Number.isFinite(opts.heightM) ? opts.heightM : null,
    p_notes: opts.notes || null,
  });
  return { id: data, error };
}

// Pole edit goes through the update_pole RPC added in migration 0029.
// A direct table UPDATE will not work because PostgREST cannot cast a
// GeoJSON jsonb body into a PostGIS geometry column on its own; the RPC
// does `ST_SetSRID(ST_GeomFromGeoJSON(...), 4326)` for us. Nullable params
// mirror the street/obstacle update RPCs: pass nothing to leave the
// attribute unchanged, only reposition.
export async function updatePole(id, geometry, opts = {}) {
  const { error } = await supabase.rpc('update_pole', {
    b_id: id,
    g: geometry,
    p_kind: opts.kind || null,
    p_height: Number.isFinite(opts.heightM) ? opts.heightM : null,
    p_notes: opts.notes != null ? opts.notes : null,
  });
  return { error };
}

export async function deletePole(id) {
  const { error } = await supabase.from('network_poles').delete().eq('id', id);
  return { error };
}
